import React, { useEffect } from 'react';
import './aloraLanding.css';

const privacyPoints = ['No hay nube.', 'No hay feeds públicos.', 'Solo tus historias.'];

const PhoneFrame = ({ variant }) => (
  <div className={`alora-phone alora-phone--${variant}`}>
    <div className="alora-phone-speaker" />
    <div className="alora-phone-screen">
      {variant === 'welcome' && (
        <div className="alora-screen alora-screen--welcome">
          <div className="alora-screen-time">5:46</div>
          <div className="alora-screen-brand">Alora</div>
          <div className="alora-screen-divider" />
          <h3>Bienvenido a Alora</h3>
          <p>Donde tus recuerdos se vuelven historias.</p>
          <span className="alora-screen-caption">
            Responde preguntas simples y graba recuerdos que tu familia podrá conservar.
          </span>
          <div className="alora-memory-photo alora-memory-photo--warm">
            <div className="alora-memory-glow" />
            <div className="alora-memory-sofa" />
            <div className="alora-memory-person" />
          </div>
          <button type="button" className="alora-screen-button">
            Continuar
          </button>
        </div>
      )}
      {variant === 'story' && (
        <div className="alora-screen alora-screen--story">
          <div className="alora-recording-pill">REC</div>
          <div className="alora-screen-prompt">¿Cuál es la historia que tu familia debería recordar?</div>
          <div className="alora-portrait-scene">
            <div className="alora-portrait-ring" />
            <div className="alora-portrait-face" />
            <div className="alora-portrait-shirt" />
          </div>
          <div className="alora-screen-progress">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
      {variant === 'library' && (
        <div className="alora-screen alora-screen--library">
          <div className="alora-screen-time">9:41</div>
          <div className="alora-library-title">Mis Recuerdos</div>
          <div className="alora-library-list">
            <div className="alora-library-card alora-library-card--sunset">
              <span>Family Vacation</span>
              <small>Ago 16, 2026</small>
            </div>
            <div className="alora-library-card alora-library-card--school">
              <span>Primer día de escuela</span>
              <small>Sep 5, 2020</small>
            </div>
            <div className="alora-library-card alora-library-card--grandpa">
              <span>Historia de abuelo</span>
              <small>May 28, 2020</small>
            </div>
          </div>
          <div className="alora-library-tabs">
            <span className="is-active" />
            <span />
            <span />
          </div>
        </div>
      )}
    </div>
  </div>
);

const StepCard = ({ icon, title, description, iconClassName }) => (
  <article className="alora-step-card">
    <div className={`alora-step-icon ${iconClassName || ''}`.trim()} aria-hidden="true">
      {icon}
    </div>
    <h3>{title}</h3>
    <p>{description}</p>
  </article>
);

export const AloraLanding = () => {
  useEffect(() => {
    const pageTitle = 'Alora | Tu historia, siempre presente';
    const pageDescription =
      'Guarda recuerdos en video con preguntas guiadas, categorías personalizadas y momentos familiares que permanecen contigo.';
    const pageUrl = `${window.location.origin}/alora`;
    const previewImage = `${window.location.origin}/img/alora_welcome.jpeg`;
    const favicon =
      document.querySelector('link[rel="shortcut icon"]') ||
      document.querySelector('link[rel="icon"]');
    const previousHref = favicon ? favicon.getAttribute('href') : null;
    const previousType = favicon ? favicon.getAttribute('type') : null;
    const previousTitle = document.title;
    const metaConfigs = [
      { attr: 'name', key: 'description', content: pageDescription },
      { attr: 'property', key: 'og:title', content: pageTitle },
      { attr: 'property', key: 'og:description', content: pageDescription },
      { attr: 'property', key: 'og:type', content: 'website' },
      { attr: 'property', key: 'og:url', content: pageUrl },
      { attr: 'property', key: 'og:image', content: previewImage },
      { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { attr: 'name', key: 'twitter:title', content: pageTitle },
      { attr: 'name', key: 'twitter:description', content: pageDescription },
      { attr: 'name', key: 'twitter:image', content: previewImage },
    ];
    const metaState = metaConfigs.map(({ attr, key, content }) => {
      let element = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !element;
      const previousContent = element ? element.getAttribute('content') : null;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);

      return { attr, key, element, created, previousContent };
    });
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    const previousCanonicalHref = canonical ? canonical.getAttribute('href') : null;

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', pageUrl);
    document.title = pageTitle;

    if (favicon) {
      favicon.setAttribute('href', '/img/alora_full.png');
      favicon.setAttribute('type', 'image/png');
    }

    return () => {
      if (!favicon) {
        return;
      }

      if (previousHref) {
        favicon.setAttribute('href', previousHref);
      }

      if (previousType) {
        favicon.setAttribute('type', previousType);
      } else {
        favicon.removeAttribute('type');
      }

      document.title = previousTitle;

      metaState.forEach(({ element, created, previousContent }) => {
        if (created) {
          element.remove();
          return;
        }

        if (previousContent) {
          element.setAttribute('content', previousContent);
        } else {
          element.removeAttribute('content');
        }
      });

      if (createdCanonical) {
        canonical.remove();
      } else if (previousCanonicalHref) {
        canonical.setAttribute('href', previousCanonicalHref);
      } else {
        canonical.removeAttribute('href');
      }
    };
  }, []);

  return (
    <div className="alora-page">
      <header className="alora-shell alora-topbar-shell">
        <div className="alora-topbar">
          <div className="alora-brand">
            <div className="alora-brand-row">
              <a className="alora-wordmark" href="/alora" aria-label="Alora inicio">
                Alora
              </a>
              <img
                className="alora-brand-logo"
                src="/img/alora_full.png"
                alt="Logo de Alora"
              />
            </div>
            <span className="alora-tagline">Tu historia, siempre presente</span>
          </div>
          <nav className="alora-nav" aria-label="Alora">
            <a href="#alora-como-funciona">Cómo funciona</a>
            <a href="#alora-ejemplos" className="is-active">
              Ejemplos
            </a>
            <a href="#alora-privacidad">Privacidad</a>
          </nav>
          <a
            className="alora-topbar-button"
            href="https://apps.apple.com/mx/app/alora/id6759730723"
            target="_blank"
            rel="noreferrer"
          >
            Descargar
          </a>
        </div>
      </header>

      <section className="alora-shell alora-panel alora-panel--hero">
        <div className="alora-copy">
          <div className="alora-copy-body">
            <h1>
              Captura los <span>momentos</span>
              <br />
              que importan
            </h1>
            <p>
              Responde una pregunta y guarda tu historia en video.
Los recuerdos de padres, hijos y los tuyos propios se convierten en historias que tu familia podrá conservar para siempre.
            </p>
          </div>
          <div className="alora-actions">
            <a
              className="alora-button"
              href="https://apps.apple.com/mx/app/alora/id6759730723"
              target="_blank"
              rel="noreferrer"
            >
              Descargar en App Store
            </a>
          </div>
        </div>
        <div className="alora-device-stack">
          <div className="alora-hero-shot-frame">
            <img
              className="alora-hero-shot"
              src="/img/alora_welcome.jpeg"
              alt="Captura de pantalla de bienvenida de Alora"
            />
          </div>
        </div>
      </section>

      <section id="alora-como-funciona" className="alora-shell alora-panel alora-panel--steps">
        <div className="alora-section-heading">
          <h2>Cómo funciona Alora</h2>
        </div>
        <div className="alora-steps-layout">
          <StepCard
            icon="?"
            title="Pregunta"
            description="Alora te hace preguntas sobre infancia, familia, amigos y momentos importantes para guiar tus recuerdos."
          />
          <div className="alora-step-phone">
            <img 
              className="alora-hero-shot"
              src="/img/alora_record.jpg" alt="Alora app mostrando una historia" />
          </div>
          <StepCard
            icon=""
            iconClassName="alora-step-icon--gallery"
            title="Guarda"
            description="Tus historias se guardan en tu teléfono, dándote control total y privacidad."
          />
        </div>
        <div className="alora-slider-dots" aria-hidden="true">
          <span className="is-active" />
          <span />
          <span />
        </div>
      </section>

      <section id="alora-ejemplos" className="alora-shell alora-panel alora-panel--prompts">
        <div className="alora-prompts-copy">
          <span className="alora-section-kicker">Prompts personalizados</span>
          <h2>
            Elige la <span>categoría</span>
            <br />
            y guarda cada pensamiento
          </h2>
          <p>
            Alora te deja escoger categorías para que cada recuerdo nazca desde el tema correcto:
            infancia, familia, amistades, tradiciones o cualquier momento que quieras preservar.
          </p>
          <p>
            También puedes grabar la forma única en que tus hijos piensan, imaginan y responden
            hoy, para conservar esas voces y ocurrencias para la posteridad.
          </p>
        </div>
        <div className="alora-prompts-gallery">
          <div className="alora-preview-shot-frame alora-preview-shot-frame--categories">
            <img
              className="alora-preview-shot"
              src="/img/alora_categories.jpg"
              alt="Captura de pantalla de categorías de prompts en Alora"
            />
          </div>
          <div className="alora-preview-shot-frame alora-preview-shot-frame--kids">
            <img
              className="alora-preview-shot"
              src="/img/alora_kids.jpg"
              alt="Captura de pantalla de prompts para niños en Alora"
            />
          </div>
        </div>
      </section>

      <section id="alora-privacidad" className="alora-shell alora-panel alora-panel--privacy">
        <div className="alora-copy alora-copy--narrow">
          <h2>
            Tus recuerdos
            <br />
            te <span>pertenecen</span>
          </h2>
          <p>Los videos se guardan directamente en tu dispositivo.</p>
          <div className="alora-checklist">
            {privacyPoints.map((point) => (
              <div className="alora-check-item" key={point}>
                <span className="alora-checkmark" aria-hidden="true" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="alora-device-stack alora-device-stack--wide">
          <PhoneFrame variant="library" />
        </div>
      </section>

      <footer className="alora-footer">
        <span>Alora by Mindware</span>
        <a
          className="alora-producthunt-badge"
          href="https://www.producthunt.com/products/alora-app?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-alora-app"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            alt="Alora App - Tu historia, siempre presente | Product Hunt"
            width="250"
            height="54"
            src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1094602&theme=light&t=1773162343604"
          />
        </a>
        <a href="/privacy_policy_alora">Política de privacidad</a>
      </footer>
    </div>
  );
};
